import { createAsyncThunk, createSlice, } from '@reduxjs/toolkit'
import { AppState } from '@app/store/state/app.state';
import { TeamEntity, TeamWithCount } from '@shared/models/team.models';
import { SortOptions, UserInfoDetails, UserInfoV2 } from "./ManageUsers.model";
import { agent } from '@shared/services/agent';
import { loadConfig } from "@shared/store/config.duck";
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { Department } from "../../../../departments/data/departments.models";

export interface ManageUsersState {
  isLoading: boolean;
  users: UserInfoV2[];
  checkedUsers: string[];
  selectedTeam?: TeamWithCount;
  selectedOffice?: OfficeEntity;
  selectedDepartment?: Department;
  selectedMisc?: string;
  sortOption: SortOptions;
  info?: UserInfoDetails;
  skip: number;
  totalUsers: number;
  searchQuery?: string;
  checkAllUsersChecked: boolean;
  // The load whose response the list shows; an older response that arrives later is ignored
  latestLoadRequestId?: string;
}

export const PAGE_SIZE = 50;
export const MIN_SEARCH_LENGTH = 3;

interface LoadUsersParams {
  team?: TeamEntity | TeamWithCount;
  office?: OfficeEntity;
  department?: Department;
  misc?: string;
  skip?: number;
  count?: number;
  orderBy?: string;
  searchTerm?: string;
}

export const initialManageUsersState: ManageUsersState = {
  isLoading: false,
  users: [],
  checkedUsers: [],
  sortOption: SortOptions.usernameAz,
  skip: 0,
  totalUsers: 0,
  checkAllUsersChecked: false,
}

export const loadDetails: any = createAsyncThunk(
  'manageUsers/loadDetails',
  async (blank: string, thunkAPI) => {
    let url = '/api/user/management/details';
    return await agent.requests.get(url);
  }
)

export const loadUsers: any = createAsyncThunk(
  'manageUsers/loadUsers',
  async (params: LoadUsersParams, thunkAPI) => {
    let qs = []

    if (params && params.team && params.team.id !== 'ALL') {
      qs.push(`teamId=${params.team.id}`);
    } else if (params && params.office) {
      qs.push(`officeId=${params.office.id}`)
    } else if (params && params.department) {
      qs.push(`departmentId=${params.department.id}`)
    } else if (params && params.misc) {
      qs.push(`misc=${params.misc}`)
    }

    if (params && params.searchTerm) qs.push(`q=${encodeURIComponent(params.searchTerm)}`);
    if (params && params.skip) qs.push(`skip=${params.skip}`);
    if (params && params.orderBy) qs.push(`order=${params.orderBy}`);
    if (params && params.count) qs.push(`count=${params.count}`);

    let url = `/api/user/user-list${qs.length > 0 ? `?${qs.join('&')}` : ''}`;

    const response = await agent.requests.get(url);
    return response;
  }
)

// The list the page is showing: its filter, search, sort and page
function currentListParams(state: ManageUsersState): LoadUsersParams {
  const searchQuery = state.searchQuery ?? '';
  return {
    team: state.selectedTeam,
    office: state.selectedOffice,
    department: state.selectedDepartment,
    misc: state.selectedMisc,
    orderBy: state.sortOption,
    searchTerm: searchQuery.length >= MIN_SEARCH_LENGTH ? searchQuery : undefined,
    skip: state.skip,
  };
}

// Loads the list again with the page's current filter, search, sort and page, plus any overrides.
// If the page comes back empty because its users were moved out of the filter, it steps back a page.
export const reloadUsers: any = createAsyncThunk(
  'manageUsers/reloadUsers',
  async (overrides: Partial<LoadUsersParams> | undefined, thunkAPI) => {
    const params = {...currentListParams((thunkAPI.getState() as AppState).manageUsers), ...overrides};
    const result = await thunkAPI.dispatch(loadUsers(params));
    const skip = params.skip ?? 0;
    if (skip > 0 && result.payload?.users?.length === 0) {
      await thunkAPI.dispatch(loadUsers({...params, skip: Math.max(0, skip - PAGE_SIZE)}));
    }
  }
)

export const assignUsersToDepartment: any = createAsyncThunk(
  'manageUsers/assignUsersToDepartment',
  async (departmentIdToAssignTo: number, thunkAPI) => {
    const checkedUsers = selectAllCheckedUsers(thunkAPI.getState() as AppState);
    if (checkedUsers.length > 0) {
      const body = {
        departmentId: departmentIdToAssignTo,
        userIds: checkedUsers.map(u => u.id),
      }
      await agent.requests.put('/api/user/management/assign-to-department', body);
      thunkAPI.dispatch(loadConfig(['departments']));
      thunkAPI.dispatch(reloadUsers());
    }
  }
)

export const assignUsersToTeam: any = createAsyncThunk(
  'manageUsers/assignUsersToTeam',
  async (teamIdToAssignTo: string, thunkAPI) => {
    const checkedUsers = selectAllCheckedUsers(thunkAPI.getState() as AppState);
    if (checkedUsers.length > 0) {
      const body = {
        teamId: teamIdToAssignTo,
        userIds: checkedUsers.map(u => u.id),
      }
      await agent.requests.put('/api/user/management/assign-to-team', body);
      thunkAPI.dispatch(loadConfig(['teams']));
      thunkAPI.dispatch(reloadUsers());
    }
  }
)

export const assignUsersToOffice: any = createAsyncThunk(
  'manageUsers/assignUsersToOffice',
  async (officeIdToAssignTo: number, thunkAPI) => {
    const checkedUsers = selectAllCheckedUsers(thunkAPI.getState() as AppState);
    if (checkedUsers.length > 0) {
      const body = {
        officeId: officeIdToAssignTo,
        userIds: checkedUsers.map(u => u.id),
      }
      await agent.requests.put('/api/user/management/assign-to-office', body);
      thunkAPI.dispatch(reloadUsers());
    }
  }
)

export const checkUser: any = createAsyncThunk(
  'manageUsers/checkUser',
  async (params: {userId: string, state: boolean}, thunkAPI) => {
    const users = (thunkAPI.getState() as AppState).manageUsers.checkedUsers;
    if (params.state) {
      if (!users.find(uid => uid === params.userId)) {
        return [...users, params.userId];
      }
    } else {
      return [...users.filter(uid => params.userId !== uid)];
    }
  }
)

export const checkAllUsers: any = createAsyncThunk(
  'manageUsers/checkAllUsers',
  async (params: {state: boolean}, thunkAPI) => {
    const users = (thunkAPI.getState() as AppState).manageUsers.users;
    if (params.state) {
      return {
        checkedUsers: users.map(user => user.id),
        checkAllUsersChecked: params.state,
      };
    } else {
      return {
        checkedUsers: [],
        checkAllUsersChecked: params.state,
      }
    }
  }
)

const manageUsersSlice = createSlice({
  name: 'manageUsers',
  initialState: initialManageUsersState,
  reducers: {
    reset: () => ({...initialManageUsersState}),
    setSelectedTeam: (state, action) => ({...state, selectedTeam: action.payload, selectedOffice: undefined, selectedDepartment: undefined, selectedMisc: undefined}),
    setSelectedOffice: (state, action) => ({...state, selectedTeam: undefined, selectedOffice: action.payload, selectedDepartment: undefined, selectedMisc: undefined}),
    setSelectedDepartment: (state, action) => ({...state, selectedTeam: undefined, selectedOffice: undefined, selectedDepartment: action.payload, selectedMisc: undefined}),
    setSelectedMisc: (state, action) => ({...state, selectedTeam: undefined, selectedOffice: undefined, selectedDepartment: undefined, selectedMisc: action.payload}),
    setSortOption: (state, action) => ({...state, sortOption: action.payload}),
    setSearchQuery: (state, action) => ({...state, searchQuery: action.payload}),
    setCheckedUsers: (state, action) => ({...state, checkedUsers: action.payload}),
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadUsers.pending, (state, action) => ({...state, desks: [], isLoading: true, users: [], latestLoadRequestId: action.meta.requestId}))
      .addCase(loadUsers.rejected, (state, action) => action.meta.requestId !== state.latestLoadRequestId ? state : ({...state, isLoading: false}))
      .addCase(loadUsers.fulfilled, (state, action) => action.meta.requestId !== state.latestLoadRequestId ? state : ({
      ...state,
      isLoading: false,
      users: action.payload.users,
      skip: action.payload.skip,
      totalUsers: action.payload.total,
      checkedUsers: [],
    }))
      .addCase(assignUsersToTeam.pending, (state) => ({...state, desks: [], isLoading: true}))
      .addCase(assignUsersToTeam.rejected, (state) => ({...state, isLoading: false}))
      .addCase(assignUsersToTeam.fulfilled, (state, action) => ({...state, isLoading: false}))
      .addCase(assignUsersToOffice.pending, (state) => ({...state, isLoading: true}))
      .addCase(assignUsersToOffice.rejected, (state) => ({...state, isLoading: false}))
      .addCase(assignUsersToOffice.fulfilled, (state) => ({...state, isLoading: false}))
      .addCase(assignUsersToDepartment.pending, (state) => ({...state, isLoading: true}))
      .addCase(assignUsersToDepartment.rejected, (state) => ({...state, isLoading: false}))
      .addCase(assignUsersToDepartment.fulfilled, (state, action) => ({...state, isLoading: false}))
      .addCase(loadDetails.fulfilled, (state, action) => ({
      ...state,
      info: action.payload,
    }))
      .addCase(checkUser.fulfilled, (state, action) => ({
      ...state,
      checkedUsers: action.payload,
    }))
      .addCase(checkAllUsers.fulfilled, (state, action) => ({
      ...state,
      checkedUsers: action.payload.checkedUsers,
      checkAllUsersChecked: action.payload.checkAllUsersChecked
    }));
  }
})

export default manageUsersSlice.reducer;
export const { setSelectedMisc, setSelectedTeam, setSelectedOffice, setSelectedDepartment, setSortOption, setSearchQuery, reset: resetManageUsers } = manageUsersSlice.actions;

// Selectors
export const selectUsers = (state: AppState) => state.manageUsers.users;
export const selectSelectedTeam = (state: AppState) => state.manageUsers.selectedTeam;
export const selectSelectedOffice = (state: AppState) => state.manageUsers.selectedOffice;
export const selectSelectedDepartment = (state: AppState) => state.manageUsers.selectedDepartment;
export const selectSelectedMisc = (state: AppState) => state.manageUsers.selectedMisc;
export const selectIsManageUsersLoading = (state: AppState) => state.manageUsers.isLoading;
export const selectSelectedSortOption = (state: AppState) => state.manageUsers.sortOption;
export const selectAllCheckedUsers = (state: AppState) => {
  const users = state.manageUsers.users;
  const checkedUsers = state.manageUsers.checkedUsers;
  return users.filter(u => checkedUsers.find(uid => u.id === uid));
}
export const selectInfo = (state: AppState) => state.manageUsers.info;
export const selectNumberOfUsersSkipped = (state: AppState) => state.manageUsers.skip;
export const selectTotalUsersInQuery = (state: AppState) => state.manageUsers.totalUsers;
export const selectTotalUsersReturned = (state: AppState) => state.manageUsers.users.length;
// For "Showing 1 - 50": the position of the page's first user, or 0 when the page is empty
export const firstShown = (skipped: number, returned: number) => returned > 0 ? skipped + 1 : 0;
export const selectSearchQuery = (state: AppState) => state.manageUsers.searchQuery;
const selectCheckedUsers = (state: AppState) => state.manageUsers.checkedUsers;
export const selectCheckAllUsersChecked = (state: AppState) => state.manageUsers.checkAllUsersChecked;
